import { Button, Card, Result } from 'antd';
import { Component, Fragment, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

function isChunkLoadError(error: Error) {
  return (
    error.name === 'ChunkLoadError' ||
    /(?:loading|failed to load) (?:css )?chunk/i.test(error.message) ||
    /Failed to fetch dynamically imported module/i.test(error.message) ||
    /error loading dynamically imported module/i.test(error.message) ||
    /Unable to preload CSS/i.test(error.message) ||
    /Importing a module script failed/i.test(error.message) ||
    /['"]text\/html['"] is not a valid JavaScript MIME type/i.test(error.message)
  );
}

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
  isOnline: boolean;
  retryCount: number;
};

type ErrorBoundaryProps = {
  children: ReactNode;
  contained?: boolean;
  onReset?: () => void;
  fallback?: (retry: () => void) => ReactNode;
};

type ErrorFallbackProps = {
  error: Error;
  isOnline: boolean;
  onReload: () => void;
  onRetry: () => void;
  contained?: boolean;
};

function ErrorFallback({
  error,
  isOnline,
  onReload,
  onRetry,
  contained,
}: ErrorFallbackProps) {
  const intl = useIntl();
  const chunkError = isChunkLoadError(error);
  const descriptionId = chunkError
    ? isOnline
      ? 'app.error.chunk.description.online'
      : 'app.error.chunk.description.offline'
    : 'app.error.render.description';

  return (
    <Card variant="borderless" style={{ margin: contained ? 0 : 24 }}>
      <Result
        status="error"
        title={intl.formatMessage({
          id: chunkError ? 'app.error.chunk.title' : 'app.error.render.title',
        })}
        subTitle={intl.formatMessage({ id: descriptionId })}
        extra={[
          !chunkError && (
            <Button type="primary" key="retry" onClick={onRetry}>
              {intl.formatMessage({ id: 'app.error.retry' })}
            </Button>
          ),
          <Button
            type={chunkError ? 'primary' : 'default'}
            key="reload"
            onClick={onReload}
          >
            {intl.formatMessage({ id: 'app.error.reload' })}
          </Button>,
          <Button href={import.meta.env.BASE_URL} key="home">
            {intl.formatMessage({ id: 'app.error.home' })}
          </Button>,
        ].filter(Boolean)}
      />
    </Card>
  );
}

export default class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    isOnline: typeof navigator === 'undefined' ? true : navigator.onLine,
    retryCount: 0,
  };

  static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }

  componentDidMount() {
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  componentWillUnmount() {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  handleOnline = () => {
    this.setState({ isOnline: true });
  };

  handleOffline = () => {
    this.setState({ isOnline: false });
  };

  handleRetry = () => {
    // React.lazy retains rejected imports; remounting cannot fetch a new build.
    if (this.state.error && isChunkLoadError(this.state.error)) {
      this.handleReload();
      return;
    }
    this.props.onReset?.();
    this.setState((previous) => ({
      hasError: false,
      error: null,
      retryCount: previous.retryCount + 1,
    }));
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { error, hasError, isOnline, retryCount } = this.state;

    if (!hasError || !error) {
      return <Fragment key={retryCount}>{this.props.children}</Fragment>;
    }

    if (this.props.fallback) return this.props.fallback(this.handleRetry);

    return (
      <ErrorFallback
        contained={this.props.contained}
        error={error}
        isOnline={isOnline}
        onReload={this.handleReload}
        onRetry={this.handleRetry}
      />
    );
  }
}
